import React, { useState, useEffect, useCallback } from 'react';
import BBALoader from '../components/BBALoader';
import { PinnableCard, ReportBackdrop, useReportCardMode } from '../components/crm/reportCards';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import { useNavigate } from 'react-router-dom';
import { getGoalsForMonth, updateGoalValues, saveMonthlyTargetsForYear, getMonthlyTargetsForYear } from '../services/goalsService';
import Modal from '../components/Modal';
import { useTheme } from '../contexts/ThemeContext';
import {
  GLASS_TONE,
  GLASS_TEXT,
  GLASS_FIELD,
  GLASS_INSET,
  GLASS_SECONDARY_BUTTON,
  GLASS_MODAL_OVERLAY,
  GLASS_MODAL_PANEL,
  GlassPage,
  GlassPageHeader,
  GlassCard,
  GlassCardHeader,
  GlassCircleButton,
  GlassFigure,
  GlassChip,
  GlassTooltip,
  GlassAccentButton,
  SlidingPills,
  glassChartTheme,
} from '../components/glass/GlassUi';

const GOAL_TABS = [
  { value: 'company', label: 'Company' },
  { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' },
];

// Wider glass panel for the 12-month editor
const WIDE_MODAL_PANEL = GLASS_MODAL_PANEL.replace('max-w-lg', 'max-w-4xl');

const CompanyGoals = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const cardMode = useReportCardMode();
  const [activeTab, setActiveTab] = useState('company');
  const [goals, setGoals] = useState({ company: [], leads: [], sales: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [editTarget, setEditTarget] = useState('');
  const [editActual, setEditActual] = useState('');
  
  // State for entries management
  const [goalEntries, setGoalEntries] = useState({});
  const [showAddEntryModal, setShowAddEntryModal] = useState(false);
  const [showEditEntriesModal, setShowEditEntriesModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [goalMonthlyTargets, setGoalMonthlyTargets] = useState({});
  const [monthlyTargetsForm, setMonthlyTargetsForm] = useState({});
  const [newEntryData, setNewEntryData] = useState({
    value: '',
    target: '',
    month: new Date().toLocaleString('default', { month: 'long' }),
    year: new Date().getFullYear().toString()
  });
  
  // State for goal tab in add entry modal
  const [addEntryModalTab, setAddEntryModalTab] = useState('progress');

  const fetchGoals = useCallback(async () => {
    setIsLoading(true);
    try {
      // Always fetch current month and year
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const fetchedGoals = await getGoalsForMonth(currentYear, currentMonth);
      setGoals(fetchedGoals);

      // Load monthly targets for all goals
      const allGoals = [...fetchedGoals.company, ...fetchedGoals.leads, ...fetchedGoals.sales];
      const monthlyTargetsPromises = allGoals.map(async (goal) => {
        try {
          const targets = await getMonthlyTargetsForYear(goal.metricKey, currentYear);
          return { goalId: goal.id, targets };
        } catch (error) {
          console.error(`Error loading monthly targets for ${goal.metricKey}:`, error);
          return { goalId: goal.id, targets: {} };
        }
      });

      const monthlyTargetsResults = await Promise.all(monthlyTargetsPromises);
      const monthlyTargetsMap = {};
      monthlyTargetsResults.forEach(({ goalId, targets }) => {
        monthlyTargetsMap[String(goalId)] = {
          [String(currentYear)]: targets
        };
      });
      setGoalMonthlyTargets(prev => ({
        ...prev,
        ...monthlyTargetsMap
      }));
    } catch (error) {
      console.error("Error fetching goals: ", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const openModal = (goal) => {
    setSelectedGoal(goal);
    setEditTarget(goal.target.toString());
    setEditActual(goal.actual.toString());
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedGoal(null);
    setEditTarget('');
    setEditActual('');
  };

  // Months and years arrays for form dropdowns
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  const handleSaveGoal = async () => {
    if (!selectedGoal) return;
    
    try {
      const newTarget = parseFloat(editTarget) || 0;
      const newActual = parseFloat(editActual) || 0;
      
      await updateGoalValues(selectedGoal.id, newTarget, newActual);
      
      // Update local state
      setGoals(prevGoals => ({
        ...prevGoals,
        [selectedGoal.category]: prevGoals[selectedGoal.category].map(goal =>
          goal.id === selectedGoal.id
            ? { ...goal, target: newTarget, actual: newActual }
            : goal
        )
      }));
      
      closeModal();
    } catch (error) {
      console.error("Error updating goal:", error);
    }
  };

  // Handle edit button click
  const handleEdit = async (goal) => {
    setEditingGoal(goal);
    // Initialize monthly goals form for current year
    const yearStr = new Date().getFullYear().toString();
    const currentYear = new Date().getFullYear();
    let existing = goalMonthlyTargets[String(goal.id)]?.[yearStr] || {};
    
    // If monthly targets aren't loaded, fetch them
    if (!existing || Object.keys(existing).length === 0) {
      try {
        existing = await getMonthlyTargetsForYear(goal.metricKey, currentYear);
        // Update state with fetched targets
        setGoalMonthlyTargets(prev => ({
          ...prev,
          [String(goal.id)]: {
            ...(prev[String(goal.id)] || {}),
            [yearStr]: existing
          }
        }));
      } catch (error) {
        console.error(`Error loading monthly targets for ${goal.metricKey}:`, error);
        existing = {};
      }
    }
    
    const form = months.reduce((acc, m) => {
      acc[m] = existing[m] ?? (goal?.target ?? '');
      return acc;
    }, {});
    setMonthlyTargetsForm(form);
    setShowEditEntriesModal(true);
  };

  // Handle add entry button click
  const handleAddEntry = (goal) => {
    setEditingGoal(goal);
    setNewEntryData({
      value: '',
      target: goal?.target ?? '',
      month: new Date().toLocaleString('default', { month: 'long' }),
      year: new Date().getFullYear().toString()
    });
    setShowAddEntryModal(true);
  };

  // Handle add entry submit
  const handleAddEntrySubmit = (e) => {
    e.preventDefault();
    
    if (!editingGoal) return;
    
    const nowMonth = new Date().toLocaleString('default', { month: 'long' });
    const nowYear = new Date().getFullYear().toString();
    const monthlyTargetForNow = goalMonthlyTargets[String(editingGoal.id)]?.[nowYear]?.[nowMonth];
    const effectiveTarget = monthlyTargetForNow !== undefined && monthlyTargetForNow !== ''
      ? parseFloat(monthlyTargetForNow) || 0
      : (editingGoal?.target || 0);

    const newActual = parseFloat(newEntryData.value) || 0;
    const newEntry = {
      id: Date.now(),
      goalId: editingGoal.id,
      metricKey: editingGoal.metricKey,
      value: newActual,
      month: nowMonth,
      year: nowYear,
      createdAt: new Date().toISOString()
    };
    
    setGoalEntries(prev => ({
      ...prev,
      [String(editingGoal.id)]: [...(prev[String(editingGoal.id)] || []), newEntry]
    }));

    // Persist actual and align target to month-specific target if set
    try {
      updateGoalValues(editingGoal.id, effectiveTarget, newActual);
    } catch (err) {
      console.error('Failed to persist progress update', err);
    }

    // Update local goals state so UI reflects immediately
    setGoals(prevGoals => ({
      ...prevGoals,
      [editingGoal.category]: prevGoals[editingGoal.category].map(g =>
        g.id === editingGoal.id ? { ...g, actual: newActual, target: effectiveTarget } : g
      )
    }));
    
    setShowAddEntryModal(false);
    setEditingGoal(null);
    setNewEntryData({
      value: '',
      month: new Date().toLocaleString('default', { month: 'long' }),
      year: new Date().getFullYear().toString()
    });
  };

  // Save monthly targets for current year
  const handleSaveMonthlyTargets = async () => {
    if (!editingGoal) return;
    const currentYear = new Date().getFullYear();
    const yearStr = currentYear.toString();
    
    try {
      // Save to Firebase
      await saveMonthlyTargetsForYear(editingGoal.metricKey, currentYear, monthlyTargetsForm);
      
      // Update local state
      setGoalMonthlyTargets(prev => ({
        ...prev,
        [String(editingGoal.id)]: {
          ...(prev[String(editingGoal.id)] || {}),
          [yearStr]: monthlyTargetsForm
        }
      }));

      // Refresh goals to get updated targets
      await fetchGoals();
      
      setShowEditEntriesModal(false);
      setEditingGoal(null);
    } catch (error) {
      console.error("Error saving monthly targets:", error);
      alert("Failed to save monthly targets. Please try again.");
    }
  };

  // Handle edit entry
  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEditingData(entry);
  };

  // Handle edit entry submit
  const handleEditEntrySubmit = (e) => {
    e.preventDefault();
    
    if (!editingEntry || !editingGoal) return;
    
    setGoalEntries(prev => ({
      ...prev,
      [String(editingGoal.id)]: (prev[String(editingGoal.id)] || []).map(entry =>
        entry.id === editingEntry.id ? editingData : entry
      )
    }));
    
    setEditingEntry(null);
    setEditingData({});
  };

  // Handle delete entry
  const handleDeleteEntry = (entryId) => {
    if (!editingGoal) return;
    
    setGoalEntries(prev => ({
      ...prev,
      [String(editingGoal.id)]: (prev[String(editingGoal.id)] || []).filter(entry => entry.id !== entryId)
    }));
  };

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  // Calculate goal statistics from actual data
  const calculateGoalStats = () => {
    const allGoals = [...goals.company, ...goals.leads, ...goals.sales];
    const totalGoals = allGoals.length;
    const completedGoals = allGoals.filter(goal => goal.actual >= goal.target).length;
    const inProgressGoals = allGoals.filter(goal => goal.actual > 0 && goal.actual < goal.target).length;
    const notStartedGoals = allGoals.filter(goal => goal.actual === 0).length;
    const successRate = totalGoals > 0 ? Math.round((completedGoals / totalGoals) * 100) : 0;

    return {
      totalGoals,
      completedGoals,
      inProgressGoals,
      notStartedGoals,
      successRate
    };
  };

  const goalStats = calculateGoalStats();

  // Calculate department performance based on actual goals
  const calculateDepartmentPerformance = () => {
    const departments = [
      { name: 'Company', goals: goals.company },
      { name: 'Leads', goals: goals.leads },
      { name: 'Sales', goals: goals.sales }
    ];

    return departments.map(dept => {
      const totalGoals = dept.goals.length;
      if (totalGoals === 0) {
        return {
          department: dept.name,
          completed: 0,
          onTrack: 0,
          offTrack: 0,
          missed: 0
        };
      }

      const completed = dept.goals.filter(g => g.actual >= g.target).length;
      const onTrack = dept.goals.filter(g => g.actual > 0 && g.actual < g.target && (g.actual / g.target) >= 0.7).length;
      const offTrack = dept.goals.filter(g => g.actual > 0 && g.actual < g.target && (g.actual / g.target) < 0.7).length;
      const missed = dept.goals.filter(g => g.actual === 0).length;

      return {
        department: dept.name,
        completed: Math.round((completed / totalGoals) * 100),
        onTrack: Math.round((onTrack / totalGoals) * 100),
        offTrack: Math.round((offTrack / totalGoals) * 100),
        missed: Math.round((missed / totalGoals) * 100)
      };
    });
  };

  const departmentPerformance = calculateDepartmentPerformance();



  // Calculate KPI trends based on actual goal data - always show 12 months
  const calculateKPITrends = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    return months.map((month, index) => {
      const monthGoals = goals.company.filter(g => g.actual > 0);
      const successRate = monthGoals.length > 0 ? 
        Math.round(monthGoals.reduce((sum, g) => sum + (g.actual / g.target), 0) / monthGoals.length * 100) : 0;
      
      return {
        month,
        successRate,
        goalCount: monthGoals.length
      };
    });
  };

  const kpiTrends = calculateKPITrends();

  // Calculate big three goals from actual data based on active tab
  const calculateBigThreeGoals = (tab) => {
    const formatValue = (value, unit) => {
      if (!value) return '0';
      switch (unit) {
        case 'usd':
          return `$${Number(value).toLocaleString()}`;
        case 'percent':
          return `${value}%`;
        default:
          return Number(value).toLocaleString();
      }
    };

    const getGoalData = (metricKey, collection) => {
      return collection.find(g => g.metricKey === metricKey);
    };

    const currentMonthName = months[new Date().getMonth()];
    const currentYearStr = new Date().getFullYear().toString();

    if (tab === 'company') {
      const mrrGoal = getGoalData('mrr', goals.company);
      const activeClientsGoal = getGoalData('activeClients', goals.company);
      const retentionGoal = getGoalData('retention', goals.company);

      const mrrTarget = mrrGoal ? (goalMonthlyTargets[String(mrrGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? mrrGoal.target ?? 0) : 0;
      const activeClientsTarget = activeClientsGoal ? (goalMonthlyTargets[String(activeClientsGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? activeClientsGoal.target ?? 0) : 0;
      const retentionTarget = retentionGoal ? (goalMonthlyTargets[String(retentionGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? retentionGoal.target ?? 0) : 0;

      return [
        {
          key: 'active-clients',
          name: 'Total Active Clients',
          goal: formatValue(activeClientsTarget || 0, 'count'),
          current: formatValue(activeClientsGoal?.actual || 0, 'count'),
          progress: activeClientsGoal ? (activeClientsGoal.actual / (activeClientsTarget || 1)) * 100 : 0,
          color: GLASS_TONE.mint,
          rawGoal: activeClientsGoal
        },
        {
          key: 'retention-rate',
          name: 'Client Retention Rate',
          goal: formatValue(retentionTarget || 0, 'percent'),
          current: formatValue(retentionGoal?.actual || 0, 'percent'),
          progress: retentionGoal ? (retentionGoal.actual / (retentionTarget || 1)) * 100 : 0,
          color: GLASS_TONE.lavender,
          rawGoal: retentionGoal
        },
        {
          key: 'mrr',
          name: 'Monthly Recurring Revenue',
          goal: formatValue(mrrTarget || 0, 'usd'),
          current: formatValue(mrrGoal?.actual || 0, 'usd'),
          progress: mrrGoal ? (mrrGoal.actual / (mrrTarget || 1)) * 100 : 0,
          color: GLASS_TONE.blue,
          rawGoal: mrrGoal
        }
      ];
    } else if (tab === 'sales') {
      const newClientsGoal = getGoalData('newClientsSigned', goals.sales);
      const conversionGoal = getGoalData('conversionRate', goals.sales);
      const packageSizeGoal = getGoalData('avgPackageSize', goals.sales);

      const newClientsTarget = newClientsGoal ? (goalMonthlyTargets[String(newClientsGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? newClientsGoal.target ?? 0) : 0;
      const conversionTarget = conversionGoal ? (goalMonthlyTargets[String(conversionGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? conversionGoal.target ?? 0) : 0;
      const packageSizeTarget = packageSizeGoal ? (goalMonthlyTargets[String(packageSizeGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? packageSizeGoal.target ?? 0) : 0;

      return [
        {
          key: 'new-clients-signed',
          name: 'New Clients Signed',
          goal: formatValue(newClientsTarget || 0, 'count'),
          current: formatValue(newClientsGoal?.actual || 0, 'count'),
          progress: newClientsGoal ? (newClientsGoal.actual / (newClientsTarget || 1)) * 100 : 0,
          color: GLASS_TONE.blue,
          rawGoal: newClientsGoal
        },
        {
          key: 'conversion-rate',
          name: 'Conversion Rate',
          goal: formatValue(conversionTarget || 0, 'percent'),
          current: formatValue(conversionGoal?.actual || 0, 'percent'),
          progress: conversionGoal ? (conversionGoal.actual / (conversionTarget || 1)) * 100 : 0,
          color: GLASS_TONE.mint,
          rawGoal: conversionGoal
        },
        {
          key: 'avg-package-size',
          name: 'Average Package Size',
          goal: formatValue(packageSizeTarget || 0, 'usd'),
          current: formatValue(packageSizeGoal?.actual || 0, 'usd'),
          progress: packageSizeGoal ? (packageSizeGoal.actual / (packageSizeTarget || 1)) * 100 : 0,
          color: GLASS_TONE.lavender,
          rawGoal: packageSizeGoal
        }
      ];
    } else if (tab === 'marketing') {
      const newLeadsGoal = getGoalData('newLeads', goals.leads);
      const qualifiedLeadsGoal = getGoalData('qualifiedLeadPercent', goals.leads);
      const costPerLeadGoal = getGoalData('costPerLead', goals.leads);

      const newLeadsTarget = newLeadsGoal ? (goalMonthlyTargets[String(newLeadsGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? newLeadsGoal.target ?? 0) : 0;
      const qualifiedLeadsTarget = qualifiedLeadsGoal ? (goalMonthlyTargets[String(qualifiedLeadsGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? qualifiedLeadsGoal.target ?? 0) : 0;
      const costPerLeadTarget = costPerLeadGoal ? (goalMonthlyTargets[String(costPerLeadGoal.id)]?.[currentYearStr]?.[currentMonthName] ?? costPerLeadGoal.target ?? 0) : 0;

      return [
        {
          key: 'new-leads',
          name: 'New Leads',
          goal: formatValue(newLeadsTarget || 0, 'count'),
          current: formatValue(newLeadsGoal?.actual || 0, 'count'),
          progress: newLeadsGoal ? (newLeadsGoal.actual / (newLeadsTarget || 1)) * 100 : 0,
          color: GLASS_TONE.blue,
          rawGoal: newLeadsGoal
        },
        {
          key: 'qualified-lead-percent',
          name: 'Qualified Lead %',
          goal: formatValue(qualifiedLeadsTarget || 0, 'percent'),
          current: formatValue(qualifiedLeadsGoal?.actual || 0, 'percent'),
          progress: qualifiedLeadsGoal ? (qualifiedLeadsGoal.actual / (qualifiedLeadsTarget || 1)) * 100 : 0,
          color: GLASS_TONE.mint,
          rawGoal: qualifiedLeadsGoal
        },
        {
          key: 'cost-per-lead',
          name: 'Cost per Lead',
          goal: formatValue(costPerLeadTarget || 0, 'usd'),
          current: formatValue(costPerLeadGoal?.actual || 0, 'usd'),
          progress: costPerLeadGoal ? (costPerLeadGoal.actual / (costPerLeadTarget || 1)) * 100 : 0,
          color: GLASS_TONE.lavender,
          rawGoal: costPerLeadGoal
        }
      ];
    }

    return [];
  };

  // My view (card mode) renders every tab's goal cards so any of them can be pinned
  const bigThreeGoals = (cardMode ? GOAL_TABS.map((t) => t.value) : [activeTab]).flatMap(calculateBigThreeGoals);

  // Calculate goal status data for pie chart
  const goalStatusData = [
    { name: 'Completed', value: goalStats.completedGoals, color: GLASS_TONE.mint },
    { name: 'In Progress', value: goalStats.inProgressGoals, color: GLASS_TONE.amber },
    { name: 'Not Started', value: goalStats.notStartedGoals, color: GLASS_TONE.stone }
  ];



  if (isLoading) {
    return (
      <GlassPage backdrop={<ReportBackdrop />}>
        <div className="flex h-64 items-center justify-center"><BBALoader size={96} /></div>
      </GlassPage>
    );
  }

  const chart = glassChartTheme(darkMode);
  const trendColors = { successRate: GLASS_TONE.blue, goalCount: GLASS_TONE.amber };
  const muted = 'text-gray-500 dark:text-white/50';
  const labelCls = `block text-sm font-medium mb-2 ${muted}`;
  const closeIcon = (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  return (
    <GlassPage backdrop={<ReportBackdrop />}>
      <GlassPageHeader
        onBack={() => navigate('/crm/reports')}
        backTitle="Back to reports"
        center={<SlidingPills options={GOAL_TABS} value={activeTab} onChange={setActiveTab} ariaLabel="Goal category" />}
        title={<>Company<br />Goals</>}
      />

      {/* Cards replay their rise whenever the tab changes */}
      <div key={activeTab} className="space-y-4">
        {/* Big Three Goals */}
        <GlassCardHeader
          title={(
            <>
              {activeTab === 'company' && 'Company Goals'}
              {activeTab === 'sales' && 'Sales Goals'}
              {activeTab === 'marketing' && 'Marketing Goals'}
            </>
          )}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {bigThreeGoals.map((goal, index) => (
            <PinnableCard key={goal.key} id={goal.key} title={goal.name}>
            <GlassCard className="p-5" delay={index * 80}>
              <GlassCardHeader
                title={goal.name}
                right={(
                  <>
                    {/* Edit Button */}
                    <GlassCircleButton onClick={() => handleEdit(goal.rawGoal)} title="Edit monthly goals">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </GlassCircleButton>

                    {/* Add Entry Button */}
                    <button
                      type="button"
                      onClick={() => handleAddEntry(goal.rawGoal)}
                      title="Add entry"
                      aria-label="Add entry"
                      className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-[#F80707] text-white shadow-[0_8px_24px_-10px_rgba(248,7,7,0.75)] transition hover:bg-[#C90505] active:scale-[0.98]"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </button>
                  </>
                )}
              />

              <div className="mt-4 space-y-4">
                <div>
                  <span className={`text-xs ${muted}`}>Current Progress</span>
                  <GlassFigure className="mt-1" value={goal.current} caption={`of ${goal.goal}`} />
                  <div className="mt-3 h-2.5 w-full rounded-full bg-black/[0.06] dark:bg-white/[0.07]">
                    <div
                      className="h-2.5 rounded-full transition-all duration-500 ease-out"
                      style={{
                        width: `${Math.min(100, goal.progress)}%`,
                        backgroundColor: goal.color
                      }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className={`${GLASS_INSET} p-4 text-center`}>
                    <span className={`text-[11px] block mb-1 ${muted}`}>Goal</span>
                    <span className="text-lg font-semibold tabular-nums text-gray-900 dark:text-white">{goal.goal}</span>
                  </div>
                  <div className={`${GLASS_INSET} p-4 text-center`}>
                    <span className={`text-[11px] block mb-1 ${muted}`}>Progress</span>
                    <span className={`text-lg font-semibold tabular-nums ${goal.progress >= 100 ? GLASS_TEXT.mint : GLASS_TEXT.strong}`}>{Math.round(goal.progress)}%</span>
                  </div>
                </div>
              </div>
            </GlassCard>
            </PinnableCard>
          ))}
        </div>

        {/* Goal Trends */}
        <PinnableCard id="goal-trends" title="Goal Trends" size="wide">
        <GlassCard className="p-5" delay={240}>
          <GlassCardHeader title="Goal Trends" />
          <div className="mt-4 flex flex-wrap gap-2">
            <GlassChip color={trendColors.successRate} label="Success Rate %" />
            <GlassChip color={trendColors.goalCount} label="Goal Count" />
          </div>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={kpiTrends} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={chart.grid} strokeDasharray="4 6" />
                <XAxis dataKey="month" tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
                <Tooltip content={<GlassTooltip />} cursor={{ stroke: chart.grid }} />
                <Line type="monotone" dataKey="successRate" stroke={trendColors.successRate} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} name="Success Rate %" />
                <Line type="monotone" dataKey="goalCount" stroke={trendColors.goalCount} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} name="Goal Count" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
        </PinnableCard>
      </div>

      {/* Goal Update Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        {selectedGoal && (
          <div className="p-6">
            <h2 className="text-2xl font-medium mb-6 text-gray-900 dark:text-white text-center">
              Update {selectedGoal.metricName}
            </h2>

            <div className="space-y-6">
              <div>
                <label className={labelCls}>
                  Target
                </label>
                <input
                  type="number"
                  value={editTarget}
                  onChange={(e) => setEditTarget(e.target.value)}
                  className={GLASS_FIELD}
                  placeholder="Enter target value"
                />
              </div>

              <div>
                <label className={labelCls}>
                  Current Actual
                </label>
                <input
                  type="number"
                  value={editActual}
                  onChange={(e) => setEditActual(e.target.value)}
                  className={GLASS_FIELD}
                  placeholder="Enter current value"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={closeModal}
                  className={GLASS_SECONDARY_BUTTON}
                >
                  Cancel
                </button>
                <GlassAccentButton onClick={handleSaveGoal}>
                  Save Changes
                </GlassAccentButton>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Add Entry Modal (kept outside the glass cards: backdrop-filter would trap a fixed overlay) */}
      {showAddEntryModal && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={GLASS_MODAL_PANEL}>
            <div className="flex items-center mb-4 relative">
              <div className="w-10" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white flex-1 text-center">
                {editingGoal?.metricName}
              </h3>
              <GlassCircleButton onClick={() => setShowAddEntryModal(false)} title="Close">
                {closeIcon}
              </GlassCircleButton>
            </div>

            {/* Progress Manual Input for current date */}
              <form onSubmit={handleAddEntrySubmit} className="space-y-4">
                <div>
                  <label className={labelCls}>
                    Value
                  </label>
                  <input
                    type="number"
                    value={newEntryData.value}
                    onChange={(e) => setNewEntryData({...newEntryData, value: e.target.value})}
                    className={GLASS_FIELD}
                    placeholder={`Enter ${editingGoal?.unit === 'usd' ? 'amount' : editingGoal?.unit === 'percent' ? 'percentage' : 'value'}`}
                    min="0"
                    step={editingGoal?.unit === 'usd' ? '0.01' : '0.1'}
                    required
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddEntryModal(false)}
                    className={`${GLASS_SECONDARY_BUTTON} flex-1 justify-center`}
                  >
                    Cancel
                  </button>
                  <GlassAccentButton type="submit" className="flex-1 justify-center">
                    Add Entry
                  </GlassAccentButton>
                </div>
              </form>

          </div>
        </div>
      )}

      {/* Monthly Goals Editor Modal */}
      {showEditEntriesModal && (
        <div className={GLASS_MODAL_OVERLAY}>
          <div className={WIDE_MODAL_PANEL}>
            <div className="flex items-center mb-4 relative">
              <div className="w-10" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white flex-1 text-center">
                {new Date().getFullYear()} Monthly Goals — {editingGoal?.metricName}
              </h3>
              <GlassCircleButton
                onClick={() => {
                  setShowEditEntriesModal(false);
                  setEditingGoal(null);
                }}
                title="Close"
              >
                {closeIcon}
              </GlassCircleButton>
            </div>
            {/* 12-month goals grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {months.map((m) => (
                <div key={m} className={`${GLASS_INSET} p-3`}>
                  <label className={labelCls}>
                    {m}
                  </label>
                  <input
                    type="number"
                    value={monthlyTargetsForm[m] ?? ''}
                    onChange={(e) => setMonthlyTargetsForm({ ...monthlyTargetsForm, [m]: e.target.value })}
                    className={GLASS_FIELD}
                    step={editingGoal?.unit === 'usd' ? '0.01' : '0.1'}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditEntriesModal(false);
                  setEditingGoal(null);
                }}
                className={GLASS_SECONDARY_BUTTON}
              >
                Cancel
              </button>
              <GlassAccentButton onClick={handleSaveMonthlyTargets}>
                Save Monthly Goals
              </GlassAccentButton>
            </div>
          </div>
        </div>
      )}
    </GlassPage>
  );
};

export default CompanyGoals;
